<template>
  <!-- Raiz única (Vue 2). O overlay + o card fullscreen são controlados por isModalOpen. -->
  <div class="perf-report">
    <!-- Overlay escuro com desfoque (só no modo expandido). Clique fecha. -->
    <div
      v-if="isModalOpen"
      class="perf-modal-overlay"
      @click="closeModal"
    />

    <!-- MESMO card/tabela nos dois modos: ao expandir, ganha as classes fullscreen.
         Sem duplicar a tabela — só alternamos classes de estilo. -->
    <div
      ref="expandedCard"
      class="card call-statistics"
      :class="{ 'call-statistics--expanded': isModalOpen }"
    >
      <div class="card-body">
        <div class="perf-card-header">
          <h6 class="chart-title">
            {{
              isModalOpen
                ? "Desempenho por Usuário - Relatório Completo"
                : "Desempenho por usuário"
            }}
          </h6>
          <div class="perf-card-actions">
            <button
              type="button"
              class="perf-export-btn"
              title="Exportar relatório em HTML"
              @click="exportHtml">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Exportar HTML
            </button>
            <!-- Botão Maximizar (canto superior direito) -->
            <button
              v-if="!isModalOpen"
              type="button"
              class="perf-expand-btn"
              title="Visualização expandida"
              aria-label="Expandir relatório"
              @click="openModal"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
            <!-- Botão Fechar (X): só no modo expandido. -->
            <button
              v-if="isModalOpen"
              type="button"
              class="perf-modal-close"
              title="Fechar"
              aria-label="Fechar relatório"
              @click="closeModal"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
        <div
          ref="tableWrap"
          class="table-responsive vendor-table-wrap"
          :class="{ 'vendor-table-wrap--expanded': isModalOpen }"
        >
          <table
            class="table vendor-table"
            :class="{ 'vendor-table--compact': isModalOpen }"
          >
            <thead>
              <tr>
                <!-- Cabeçalhos data-driven: todas as colunas são ordenáveis; as que têm
                     `tooltip` exibem o mesmo ícone (i) da coluna Paciência. -->
                <th
                  v-for="col in columns"
                  :key="col.key"
                  class="sortable"
                  :class="{ 'text-center': !col.leftAlign }"
                  @click="setSort(col.key)"
                >
                  <!-- Container flex: texto (pode quebrar em 2 linhas) + ícone (i) + setinha,
                       todos centralizados verticalmente (align-items:center). -->
                  <span
                    class="th-flex"
                    :class="{ 'th-flex--start': col.leftAlign }"
                  >
                    <span class="th-label">{{ col.label }}</span>
                    <span
                      v-if="col.tooltip"
                      class="metric-tooltip th-icon"
                      tabindex="0"
                      @click.stop
                      @mouseenter="infoEnter(col.key, $event)"
                      @mouseleave="infoLeave"
                      @focus="infoEnter(col.key, $event)"
                      @blur="infoLeave"
                    >
                      <svg
                        class="metric-tooltip-icon"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      <span
                        class="metric-tooltip-bubble"
                        :class="{
                          'metric-tooltip-bubble--visible':
                            activeInfo === col.key,
                        }"
                        :style="{ left: infoX + 'px', top: infoY + 'px' }"
                        v-html="col.tooltip"
                      />
                    </span>
                    <span
                      class="sort-arrow th-icon"
                      v-if="sortKey === col.key"
                      >{{ sortDir === "asc" ? "▲" : "▼" }}</span
                    >
                    <svg
                      v-else
                      class="sort-icon th-icon"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m21 16-4 4-4-4" />
                      <path d="M17 20V4" />
                      <path d="m3 8 4-4 4 4" />
                      <path d="M7 4v16" />
                    </svg>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pagedRanking" :key="row.extension">
                <!-- 1. Usuário -->
                <td>{{ row.name }} ({{ row.extension }})</td>
                <!-- 2. Taxa de Atendimento -->
                <td class="text-center">{{ row.hitRate }}%</td>
                <!-- 3. Total de Chamadas -->
                <td class="text-center">{{ row.totalCalls }}</td>
                <!-- 4. Chamadas Canceladas: absoluto + % do total em cinza -->
                <td class="text-center">
                  {{ row.canceledCalls }}
                  <span class="text-muted">({{ row.canceledPct }}%)</span>
                </td>
                <!-- 5. Paciência (Mediana) -->
                <td class="text-center">
                  {{ formatPatience(row.patienceMediana) }}
                </td>
                <!-- 6-7. Grupo de rediscagem -->
                <td class="text-center">{{ row.redials }}</td>
                <td class="text-center">
                  {{ row.redialHitRate }}%
                </td>
                <!-- 8-9. Métricas significativas -->
                <td class="text-center">{{ row.conversationsOver2min }}</td>
                <td class="text-center">{{ formatDecimal(row.per100) }}x</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="isModalOpen && rankingTotal > 0"
          class="perf-pagination"
        >
          <span class="perf-pagination-info">
            Mostrando {{ pageStart }} a {{ pageEnd }} de {{ rankingTotal }} usuários
          </span>
          <b-pagination
            :key="rankingTotal"
            class="mb-0"
            align="center"
            :value="currentPage"
            :total-rows="rankingTotal"
            :per-page="pageSize"
            :limit="5"
            @input="goToPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPatience } from './dashboard-helpers'

const MIN_PAGE_SIZE = 15

// Decimal com 1 casa em pt-BR (ponto -> vírgula). Ex: 1.9 -> "1,9".
function formatDecimal (value) {
  return Number(value).toFixed(1).replace('.', ',')
}

export default {
  name: 'UserPerformanceTable',
  props: {
    // Cada entrada de byExtension traz as métricas cruas por ramal. As chaves novas
    // (canceledCalls, redials, redialsAnswered, conversationsOver2min) devem vir do
    // backend; enquanto não vierem, o mapeamento abaixo assume 0 como fallback seguro.
    // {
    //   [extension]: {
    //     name,                    // nome do usuário
    //     totalCalls,              // total de chamadas no período
    //     hitRate,                 // taxa de atendimento (%)
    //     patienceMediana,         // paciência mediana (segundos)
    //     canceledCalls,           // chamadas com status ORIGINATOR_CANCEL
    //     redials,                 // rediscagens (2ª ligação em diante p/ o mesmo número)
    //     redialsAnswered,         // rediscagens que resultaram em atendimento
    //     conversationsOver2min    // atendidas com duração > 2min
    //   }
    // }
    byExtension: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      // Visualização expandida (modal quase tela cheia).
      isModalOpen: false,
      pageSize: MIN_PAGE_SIZE,
      currentPage: 1,
      sortKey: 'patienceMediana',
      sortDir: 'desc',
      // Tooltip (i) compartilhado por todas as colunas: só um visível por vez.
      activeInfo: null,
      infoX: 0,
      infoY: 0,
      infoDwelled: false,
      infoDwellTimer: null,
      infoHideTimer: null,
      // Definição das colunas (ordem = ordem da tabela). `key` é também a chave de
      // ordenação na linha; `tooltip` (HTML) exibe o ícone de ajuda quando presente.
      columns: [
        {
          key: 'name',
          label: 'Usuário',
          leftAlign: true,
          format: row => `${row.name} (${row.extension})`
        },
        {
          key: 'hitRate',
          label: 'Taxa de atendimento',
          format: row => `${row.hitRate}%`
        },
        {
          key: 'totalCalls',
          label: 'Total de chamadas',
          format: row => String(row.totalCalls)
        },
        {
          key: 'canceledCalls',
          label: 'Chamadas canceladas',
          format: row => `${row.canceledCalls} (${row.canceledPct}%)`
        },
        {
          key: 'patienceMediana',
          label: 'Espera antes de desligar',
          format: row => formatPatience(row.patienceMediana),
          tooltip:
            'Mediana do tempo que a sua operação aguarda o telefone chamar antes de a ligação cair ou ser cancelada. O tempo que o vendedor espera antes de encerrar uma ligação pode fazer diferença nos resultados. Veja nosso estudo: <a href="https://claude.ai/public/artifacts/3ec8d7fb-0511-40c3-88a7-b47b354ed90e" target="_blank" rel="noopener noreferrer">Clique aqui</a>'
        },
        {
          key: 'redials',
          label: 'Rediscagens',
          format: row => String(row.redials),
          tooltip:
            'Quantidade de rediscagens para um mesmo número de destino no período selecionado.'
        },
        {
          key: 'redialHitRate',
          label: 'Taxa atend. rediscagens',
          format: row => `${row.redialHitRate}%`
        },
        {
          key: 'conversationsOver2min',
          label: 'Chamadas significativas',
          format: row => String(row.conversationsOver2min),
          tooltip:
            'São classificadas como chamadas significativas as conversas que possuem mais de 2 minutos de duração.'
        },
        {
          key: 'per100',
          label: 'A cada 100 ligações...',
          format: row => `${formatDecimal(row.per100)}x`,
          tooltip:
            'Proporção estimada de chamadas significativas (acima de 2 minutos) a cada 100 ligações realizadas pelo usuário.'
        }
      ]
    }
  },
  computed: {
    rankingTotal () {
      return this.patienceRanking.length
    },
    pageStart () {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageEnd () {
      return Math.min(this.currentPage * this.pageSize, this.rankingTotal)
    },
    patienceRanking () {
      return Object.entries(this.byExtension)
        .map(([extension, data]) => {
          const totalCalls = data.totalCalls || 0
          const canceledCalls = data.canceledCalls || 0
          const redials = data.redials || 0
          const redialsAnswered = data.redialsAnswered || 0
          const conversationsOver2min = data.conversationsOver2min || 0
          return {
            extension,
            name: data.name,
            totalCalls,
            hitRate: data.hitRate,
            patienceMediana: data.patienceMediana,
            // 4. Canceladas + % sobre o total
            canceledCalls,
            canceledPct: totalCalls
              ? Math.round((canceledCalls / totalCalls) * 100)
              : 0,
            // 6. Rediscagens (contagem absoluta)
            redials,
            // 7. % das rediscagens que foram atendidas
            redialHitRate: redials
              ? Math.round((redialsAnswered / redials) * 100)
              : 0,
            // 8. Conversas significativas (atendidas > 2min)
            conversationsOver2min,
            // 9. Conversas significativas normalizadas por 100 ligações totais
            per100: totalCalls ? (conversationsOver2min / totalCalls) * 100 : 0
          }
        })
        .sort((a, b) => {
          let cmp
          if (this.sortKey === 'name') {
            cmp = String(a.name).localeCompare(String(b.name))
          } else {
            cmp = (a[this.sortKey] || 0) - (b[this.sortKey] || 0)
          }
          return this.sortDir === 'asc' ? cmp : -cmp
        })
    },
    // A página é cortada DEPOIS da ordenação global: as setas do cabeçalho valem
    // sobre a equipe inteira, não sobre as linhas que já estavam na tela.
    pagedRanking () {
      if (!this.isModalOpen) return this.patienceRanking
      const start = (this.currentPage - 1) * this.pageSize
      return this.patienceRanking.slice(start, start + this.pageSize)
    }
  },
  watch: {
    // Trava o scroll do fundo enquanto o modal está aberto.
    isModalOpen (open) {
      document.body.style.overflow = open ? 'hidden' : ''
    },
    // Filtro novo pode encolher a lista abaixo da página atual; o auto-refresh, que
    // devolve o mesmo tamanho, não mexe na página em que o usuário está.
    rankingTotal (total) {
      if (this.pageStart > total) this.currentPage = 1
    }
  },
  methods: {
    formatPatience,
    formatDecimal,
    openModal () {
      this.currentPage = 1
      this.isModalOpen = true
      this.$nextTick(this.measurePageSize)
    },
    // A página enche a altura maximizada: mede o teto do modal (max-height), desconta
    // o que não é linha (cabeçalho do card, paginação, thead) e divide pela altura da linha.
    // A altura da linha é a média das renderizadas, não a da primeira: nome longo quebra
    // em duas linhas e uma amostra só erraria a conta. Abaixo do piso a tabela rola por
    // dentro: em tela curta, página de 8 picotaria a lista em páginas demais.
    measurePageSize () {
      const wrap = this.$refs.tableWrap
      const rows = wrap.querySelector('tbody')
      if (rows.rows.length === 0) return
      const card = this.$refs.expandedCard
      const maxCardHeight = parseFloat(window.getComputedStyle(card).maxHeight)
      const chromeHeight = card.offsetHeight - wrap.offsetHeight
      const headHeight = wrap.querySelector('thead').offsetHeight
      const rowHeight = rows.offsetHeight / rows.rows.length
      const rowsHeight = maxCardHeight - chromeHeight - headHeight
      this.pageSize = Math.max(Math.floor(rowsHeight / rowHeight), MIN_PAGE_SIZE)
      if (this.pageStart > this.rankingTotal) this.currentPage = 1
    },
    onResize () {
      if (this.isModalOpen) this.measurePageSize()
    },
    closeModal () {
      this.isModalOpen = false
    },
    exportHtml () {
      this.$emit('export-html', {
        columns: this.columns.map(column => ({
          label: column.label,
          leftAlign: column.leftAlign === true,
          tooltip: column.tooltip
        })),
        rows: this.patienceRanking.map(row => this.columns.map(column => column.format(row)))
      })
    },
    setSort (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
      this.currentPage = 1
    },
    goToPage (value) {
      this.currentPage = value
    },
    // Tooltip genérico (mesmo comportamento do antigo da Paciência), agora por coluna.
    infoEnter (key, event) {
      clearTimeout(this.infoHideTimer)
      clearTimeout(this.infoDwellTimer)
      this.infoDwelled = false
      const rect = event.currentTarget.getBoundingClientRect()
      this.infoX = rect.left - 8
      this.infoY = rect.top + rect.height / 2
      this.activeInfo = key
      this.infoDwellTimer = setTimeout(() => {
        this.infoDwelled = true
      }, 1000)
    },
    infoLeave () {
      clearTimeout(this.infoDwellTimer)
      if (this.infoDwelled) {
        this.infoHideTimer = setTimeout(() => {
          this.activeInfo = null
        }, 1000)
      } else {
        this.activeInfo = null
      }
    },
    onKeydown (event) {
      if (event.key === 'Escape' && this.isModalOpen) this.closeModal()
    }
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy () {
    clearTimeout(this.infoDwellTimer)
    clearTimeout(this.infoHideTimer)
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    document.body.style.overflow = ''
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Dashboard/UserPerformanceTable.vue